import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { cn } from "../private/utils.js";

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b  dark:border-mono-700/50", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    hideChevron?: boolean;
    headerClassName?: string;
  }
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header
    className={cn(
      "flex data-[state=open]:border-b dark:border-mono-700/50",
      props.headerClassName
    )}
  >
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180 [&[data-state=open]_.chevron]:rotate-180",
        className
      )}
      {...props}
    >
      <>
        {children}
        {!props.hideChevron && (
          <ChevronDownIcon className="size-5 shrink-0 transition-transform duration-200" />
        )}
      </>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

const PersistentAccordionRoot = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  Omit<
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>,
    "type"
  > & {
    persistKey: string;
    onValueChange?: (value: string[]) => void;
  }
>(({ children, persistKey, ...props }, ref) => {
  const [persistentValue, setPersistentValue] = React.useState<string[]>(() => {
    if (persistKey) {
      const stored = sessionStorage.getItem(`accordion-${persistKey}`);
      return stored ? JSON.parse(stored) : props.defaultValue || [];
    }
    return props.defaultValue || [];
  });

  React.useEffect(() => {
    if (persistKey) {
      sessionStorage.setItem(
        `accordion-${persistKey}`,
        JSON.stringify(persistentValue)
      );
    }
  }, [persistentValue, persistKey]);

  const handleValueChange = (value: string[]) => {
    setPersistentValue(value);
    props.onValueChange?.(value);
  };

  return (
    <AccordionPrimitive.Root
      ref={ref}
      type="multiple"
      {...props}
      defaultValue={[]} // Add this line
      value={persistentValue}
      onValueChange={handleValueChange}
    >
      {children}
    </AccordionPrimitive.Root>
  );
});

PersistentAccordionRoot.displayName = "PersistentAccordionRoot";

const AccordionV2 = {
  Root: AccordionPrimitive.Root,
  PersistentRoot: PersistentAccordionRoot,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent
};

export { AccordionV2 };
